import { supabase } from "@/integrations/supabase/client";

/** IDs de pedidos/contratos do vendedor (escopo por ownership). */
export async function fetchEscopoIdsVendedor(vendedorId: string): Promise<{
  pedidoIds: number[];
  contratoIds: number[];
}> {
  const ids = new Set<number>();

  const { data: porPedido, error: errPedidos } = await supabase
    .from("pedidos")
    .select("id")
    .eq("vendedor_id", vendedorId);
  if (errPedidos) throw errPedidos;
  for (const p of porPedido || []) {
    const id = Number(p.id);
    if (Number.isFinite(id)) ids.add(id);
  }

  // Pedidos cujo orçamento / projeto pertence ao vendedor (mesmo se pedidos.vendedor_id estiver vazio)
  const { data: porOrcamento, error: errOrc } = await supabase
    .from("pedidos")
    .select("id, orcamento:orcamentos!orcamento_id!inner(vendedor_id)")
    .eq("orcamento.vendedor_id", vendedorId);
  if (errOrc) throw errOrc;
  for (const p of porOrcamento || []) {
    const id = Number(p.id);
    if (Number.isFinite(id)) ids.add(id);
  }

  const { data: porProjeto, error: errProj } = await supabase
    .from("pedidos")
    .select("id, projeto:projetos!inner(vendedor_id)")
    .eq("projeto.vendedor_id", vendedorId);
  if (errProj) throw errProj;
  for (const p of porProjeto || []) {
    const id = Number(p.id);
    if (Number.isFinite(id)) ids.add(id);
  }

  const pedidoIds = Array.from(ids);

  let contratoIds: number[] = [];
  if (pedidoIds.length > 0) {
    const { data: contratos, error: errContratos } = await supabase
      .from("contratos")
      .select("id")
      .in("pedido_id", pedidoIds);
    if (errContratos) throw errContratos;
    contratoIds = (contratos || []).map((c) => Number(c.id)).filter((id) => Number.isFinite(id));
  }

  // Contratos via projeto do vendedor (sem pedido_id ou pedido sem vínculo)
  const { data: contratosProj, error: errContratosProj } = await supabase
    .from("contratos")
    .select("id, projeto:projetos!inner(vendedor_id)")
    .eq("projeto.vendedor_id", vendedorId);
  if (!errContratosProj && contratosProj) {
    const set = new Set(contratoIds);
    for (const c of contratosProj) {
      const id = Number(c.id);
      if (Number.isFinite(id)) set.add(id);
    }
    contratoIds = Array.from(set);
  }

  return { pedidoIds, contratoIds };
}

/** Filtro OR para parcelas/transações do vendedor (pedido_id ou contrato_id). */
export function orFiltroEscopoPedidoContrato(
  pedidoIds: number[],
  contratoIds: number[]
): string | null {
  const parts: string[] = [];
  if (pedidoIds.length > 0) parts.push(`pedido_id.in.(${pedidoIds.join(",")})`);
  if (contratoIds.length > 0) parts.push(`contrato_id.in.(${contratoIds.join(",")})`);
  return parts.length > 0 ? parts.join(",") : null;
}

/** Aplica filtro de escopo do vendedor em query de pedidos (alinha com RLS).
 * Retorna `{ query }` (objeto plano) — não o builder direto: Postgrest é thenable e
 * `return query.or(...)` dentro de `async` executaria a query no `await` do caller.
 */
export async function applyEscopoVendedorPedidosQuery(
  query: any,
  vendedorId: string
): Promise<{ query: any }> {
  const [{ data: orcs }, { data: projetos }] = await Promise.all([
    supabase.from("orcamentos").select("id").eq("vendedor_id", vendedorId),
    supabase.from("projetos").select("id").eq("vendedor_id", vendedorId),
  ]);

  const orcIds = (orcs || []).map((o) => Number(o.id)).filter((id) => Number.isFinite(id));
  const projetoIds = (projetos || []).map((p) => Number(p.id)).filter((id) => Number.isFinite(id));

  const parts = [`vendedor_id.eq.${vendedorId}`];
  if (orcIds.length > 0) parts.push(`orcamento_id.in.(${orcIds.join(",")})`);
  if (projetoIds.length > 0) parts.push(`projeto_id.in.(${projetoIds.join(",")})`);

  return { query: query.or(parts.join(",")) };
}
